import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Download, ImageOff } from "lucide-react";
import { PortalHeader, PortalEmpty, PortalCard } from "@/components/portal/PortalHeader";
import { getMyProject, listMyColors } from "@/lib/portal/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/colors")({
  component: ColorsPage,
});

function ColorsPage() {
  const projectFn = useServerFn(getMyProject);
  const colorsFn = useServerFn(listMyColors);
  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const colorsQ = useQuery({
    queryKey: ["portal", "colors", projectId],
    queryFn: () => colorsFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
  });
  const [activeImg, setActiveImg] = useState<"before" | "design" | "render">("render");

  if (projectQ.isLoading || colorsQ.isLoading)
    return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!projectId) return <PortalEmpty title="No project yet" />;

  const data = colorsQ.data;
  const colors = data?.colors ?? [];
  const images = {
    before: data?.beforeImage ?? null,
    design: data?.designImage ?? null,
    render: data?.renderImage ?? null,
  };
  const tabs = (["before", "design", "render"] as const).filter((k) => images[k]);
  const visibleImg = images[activeImg] || images[tabs[0] ?? "render"];

  const downloadCsv = () => {
    const rows = [["Surface", "Color name", "SW number", "HEX", "LRV", "Sheen", "Product line", "Coats"]];
    colors.forEach((c) => rows.push([c.surface, c.color_name, c.color_number ?? "", c.hex ?? "", c.lrv != null ? String(c.lrv) : "", c.sheen ?? "", c.product_line ?? "", c.coats != null ? String(c.coats) : ""]));
    const csv = rows.map((r) => r.map((v) => `"${(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "color-schedule.csv";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <>
      <PortalHeader
        eyebrow="Paint Studio"
        title="Your color selections"
        description="Sherwin-Williams selections by surface, with before, preview, and AI-rendered references."
        actions={
          <button onClick={downloadCsv} disabled={!colors.length}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-50 px-4 py-2 text-xs text-zinc-200">
            <Download className="size-3.5" /> Color schedule
          </button>
        }
      />

      {tabs.length > 0 && visibleImg && (
        <PortalCard className="overflow-hidden mb-6">
          <div className="flex items-center gap-1 p-2 border-b border-white/10">
            {tabs.map((k) => (
              <button key={k} onClick={() => setActiveImg(k)}
                className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full ${activeImg === k ? "bg-amber-300 text-zinc-950 font-semibold" : "text-zinc-400 hover:text-white"}`}>
                {k === "before" ? "Before" : k === "design" ? "Preview" : "Photoreal render"}
              </button>
            ))}
          </div>
          <img src={visibleImg} alt="" className="w-full max-h-[480px] object-contain bg-black" />
        </PortalCard>
      )}

      {colors.length === 0 ? (
        <PortalEmpty title="No colors selected yet"
          description="Your project manager will pin the final Sherwin-Williams selections here." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {colors.map((c) => (
            <PortalCard key={c.id} className="overflow-hidden group">
              <div className="h-32 relative" style={{ background: c.hex || "#222" }}>
                <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-[0.3em] text-white/70 mix-blend-difference font-semibold">{c.surface}</span>
              </div>
              <div className="p-4">
                <p className="font-serif text-lg text-white">{c.color_name}</p>
                <p className="text-xs text-zinc-500">SW {c.color_number ?? "—"}</p>
                <dl className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div><dt className="text-zinc-500">HEX</dt><dd className="text-zinc-200">{c.hex ?? "—"}</dd></div>
                  <div><dt className="text-zinc-500">LRV</dt><dd className="text-zinc-200">{c.lrv ?? "—"}</dd></div>
                  <div><dt className="text-zinc-500">Sheen</dt><dd className="text-zinc-200">{c.sheen ?? "—"}</dd></div>
                </dl>
                {(c.product_line || c.coats != null) && (
                  <p className="text-[10px] text-zinc-500 mt-3 uppercase tracking-wider">
                    {c.product_line ?? ""}{c.coats != null ? ` · ${c.coats} coats` : ""}
                  </p>
                )}
              </div>
            </PortalCard>
          ))}
        </div>
      )}
      {!visibleImg && colors.length > 0 && (
        <p className="mt-6 text-[11px] text-zinc-500 inline-flex items-center gap-1.5"><ImageOff className="size-3" /> No render attached to this proposal.</p>
      )}
    </>
  );
}